import React, { useEffect, useState } from "react";
import {
  X,
  Send,
  Eye,
  CalendarClock,
  Clapperboard,
  Loader2,
  History,
  AlertCircle,
  Megaphone,
  Mail,
  CheckCircle,
} from "lucide-react";
import {
  FLOW_PREVIEWABLE_SECTORS,
  FLOW_TELEVENTA_ONLY_SECTORS,
  SECTOR_LABELS,
  hasTeleventaFlow,
  hasMarketingFlow,
  televentaEmailTemplateIdForSector,
  televentaHeaderForSector,
  simulatorPayloadForSector,
} from "./flowTemplates";

/**
 * SendFlowModal
 * Único punto de envío del "Flow" de ventas: agrupa las tres variantes (antes, tres
 * botones sueltos en la ficha), el historial de envíos (antes, un tooltip escondido en
 * un hover) y la vista previa de cada una.
 *
 * Es PRESENTACIONAL: no envía nada ni sabe de plantillas. Decide qué variantes ofrecer
 * (televenta solo en sectores con ad) y delega en `onSend(action, sector)` /
 * `onPreview(variant, sector)`.
 *
 * Si el lead no tiene sector, se elige AQUÍ dentro: antes se abría otro modal encima.
 * El sector elegido vale solo para este envío; no se guarda en el lead.
 *
 * Español hardcodeado, como el resto del Flow (FlowPreviewModal y los botones de la ficha).
 */
// Descripción de "Flow televenta" fiel a lo que se envía en cada sector: header
// vídeo o póster (según televentaHeaderForSector) y uno o dos botones, según si el
// sector tiene simulador (taller no: un único botón "Solicitar presentación").
function televentaVariantDesc(sector) {
  const headerType = televentaHeaderForSector(sector)?.type;
  const media = headerType === "IMAGE" ? "el póster" : "el vídeo";
  const buttons = simulatorPayloadForSector(sector)
    ? "los dos botones («Solicitar presentación» y «Simulador de cita»)"
    : "el botón «Solicitar presentación»";
  return `Un solo mensaje: ${media} del sector, toda la presentación y ${buttons}. Sin miniatura. Para enviar en frío, antes de la llamada del cold caller.`;
}

const VARIANTS = [
  {
    action: "flow",
    title: "Flow normal",
    icon: Send,
    tone: "emerald",
    desc: "Dos mensajes: la presentación en texto y, aparte, la miniatura con el nombre del negocio y el botón «Simulador de cita».",
    // Se oculta SOLO para sectores "solo televenta" (FLOW_TELEVENTA_ONLY_SECTORS,
    // p.ej. taller): esos ya tienen su propio Flow y no deben caer al fallback
    // barber_head4/barber_mini2. El resto de sectores sin plantilla propia (spa,
    // autoescuela…) siguen ofreciendo esta variante con ese mismo fallback, como
    // siempre.
    needsHeadMini: true,
  },
  {
    action: "flow_presentacion",
    title: "Flow + presentación",
    icon: CalendarClock,
    tone: "indigo",
    desc: "Los mismos dos mensajes, pero la miniatura añade el botón «Solicitar presentación» para que el lead reserve la demo con su vendedor.",
    needsHeadMini: true,
  },
  {
    action: "flow_televenta",
    title: "Flow televenta",
    icon: Clapperboard,
    tone: "fuchsia",
    // Estático de fallback (sin sector conocido); el desc real se calcula por sector
    // con televentaVariantDesc() en el render.
    desc: "Un solo mensaje: el vídeo o póster del sector, toda la presentación y su(s) botón(es). Sin miniatura. Para enviar en frío, antes de la llamada del cold caller.",
    needsVideo: true,
  },
  {
    action: "flow_marketing",
    title: "Flow marketing",
    icon: Megaphone,
    tone: "sky",
    desc: "Un solo mensaje: vídeo de ejemplo de campaña de marketing + presentación del servicio de captación/reenganche de clientes por WhatsApp. Para leads que NO quieren CRM.",
    needsMarketing: true,
  },
  {
    action: "flow_email",
    title: "Solo email",
    icon: Mail,
    tone: "amber",
    desc: "El mismo copy y el mismo vídeo del Flow, pero por correo (con el botón para reservar la demo). Se manda SIN WhatsApp. Normalmente no hace falta: el email ya sale solo junto a cualquier Flow si el lead tiene correo.",
    needsEmail: true,
  },
];

const TONES = {
  emerald: "from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-200/50",
  indigo: "from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 shadow-indigo-200/50",
  fuchsia: "from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 shadow-fuchsia-200/50",
  sky: "from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-sky-200/50",
  amber: "from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-amber-200/50",
};
const ICON_TONES = {
  emerald: "bg-emerald-50 text-emerald-600",
  indigo: "bg-indigo-50 text-indigo-600",
  fuchsia: "bg-fuchsia-50 text-fuchsia-600",
  sky: "bg-sky-50 text-sky-600",
  amber: "bg-amber-50 text-amber-600",
};

export default function SendFlowModal({
  open,
  onClose,
  leadName = "",
  phone = "",
  email = "",
  leadSector = null,
  isTeleventa = false,
  sends = [],
  // Envíos POR VARIANTE. `sends` (arriba) es el historial del Flow de televenta y se
  // pinta en la cabecera; servía para las tres variantes que sellan `flow_*`, pero para
  // "Flow marketing" contaba otra cosa: quien lo abría para mandar marketing leía
  // "Ya enviado 3 veces" refiriéndose a televenta, justo en el momento de decidir.
  // Cada tarjeta enseña ahora lo suyo: el WhatsApp y, aparte, el email que lo acompaña.
  sendsByAction = {},
  emailSendsByAction = {},
  formatDate = () => "",
  sending = false,
  onSend,
  onPreview,
}) {
  // Sector para ESTE envío: el del lead, o el que se elija aquí si no tiene.
  const [pickedSector, setPickedSector] = useState(leadSector);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setPickedSector(leadSector);
      setHistoryOpen(false);
    }
  }, [open, leadSector]);

  if (!open) return null;

  const sector = pickedSector;
  const needsSector = !sector;
  const hasEmail = !!(email || "").trim();
  // El email (mismo copy + vídeo) solo existe en los sectores con plantilla de Resend.
  const sectorHasEmail = !!televentaEmailTemplateIdForSector(sector);
  // Lead marcado como televenta: su envío es el Flow televenta (vídeo del sector + todo
  // el texto en un solo mensaje). Las variantes de dos mensajes y la de marketing no
  // pintan nada ahí y solo se prestan a equivocarse, así que se ocultan. El email sigue
  // ofreciéndose: es otro canal, no otra versión del mensaje.
  const televentaOnly = isTeleventa && hasTeleventaFlow(sector);
  const variants = VARIANTS.filter((v) =>
    // "Flow normal"/"Flow + presentación" siguen ofreciéndose (con el fallback
    // barber_head4/barber_mini2) para cualquier sector SIN plantilla propia, como
    // siempre — EXCEPTO los "solo televenta" (taller): esos ya tienen su propio
    // Flow y mandarles el fallback de barbería sería un envío equivocado, no un
    // simple "sin personalizar".
    (!v.needsHeadMini || !FLOW_TELEVENTA_ONLY_SECTORS.includes(sector)) &&
    (!v.needsVideo || hasTeleventaFlow(sector)) &&
    (!v.needsMarketing || hasMarketingFlow(sector)) &&
    (!v.needsEmail || (hasEmail && sectorHasEmail)) &&
    (!televentaOnly || v.action === "flow_televenta" || v.action === "flow_email"),
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera: a quién se le manda + historial */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-black text-slate-800">Enviar Flow</h3>
              <p className="text-[11px] font-medium text-slate-400 truncate">
                {leadName?.trim() || "el lead"}
                {phone ? ` · ${phone}` : ""}
              </p>
              {/* El email va SIEMPRE en el mismo click que el Flow. Si falta, se dice
                  aquí: antes se saltaba en silencio y el vendedor no se enteraba. */}
              {sectorHasEmail && (
                hasEmail ? (
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 truncate">
                    <Mail className="w-3 h-3 shrink-0" />
                    <span className="truncate">Se enviará también por email a {email.trim()}</span>
                  </p>
                ) : (
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-amber-600">
                    <AlertCircle className="w-3 h-3 shrink-0" />
                    Sin email: solo WhatsApp. Rellena el Email de la ficha para que también le llegue el correo.
                  </p>
                )
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-xl transition-all active:scale-90 shrink-0"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {sends.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold hover:bg-amber-100 transition-all active:scale-95"
              >
                <History className="w-3 h-3" />
                {/* Decir de QUÉ es este contador: son los envíos del Flow de televenta.
                    Sin el matiz, quien abría el modal para mandar el de marketing lo leía
                    como si fuera el suyo. */}
                Flow de televenta: {sends.length}{" "}
                {sends.length === 1 ? "envío" : "envíos"}
                <span className="text-amber-500">{historyOpen ? "▴" : "▾"}</span>
              </button>
              {historyOpen && (
                <ul className="mt-2 space-y-0.5 rounded-xl bg-slate-50 px-3 py-2 max-h-32 overflow-y-auto">
                  {sends
                    .slice()
                    .reverse()
                    .map((s, i) => (
                      <li
                        key={i}
                        className="flex justify-between gap-3 text-[11px] font-medium text-slate-600"
                      >
                        <span>{s.by_name || s.by_user_id || "—"}</span>
                        <span className="text-slate-400">
                          {formatDate(s.at, true) || "fecha desconocida"}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Selector de sector: solo si el lead no tiene. Determina el copy de las
            plantillas y si hay variante de televenta. No se guarda en el lead. */}
        {needsSector && (
          <div className="px-6 py-3 border-b border-slate-50 bg-amber-50/40">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <p className="text-[11px] font-bold text-amber-700">
                Este lead no tiene sector. Elige para qué sector enviarlo.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {FLOW_PREVIEWABLE_SECTORS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPickedSector(s)}
                  className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-white text-slate-500 border border-amber-200 hover:border-amber-400 hover:text-amber-700 transition-all active:scale-95"
                >
                  {SECTOR_LABELS[s] || s}
                </button>
              ))}
            </div>
          </div>
        )}

        {!needsSector && !leadSector && (
          <div className="px-6 py-2 border-b border-slate-50 flex items-center justify-between gap-2 bg-emerald-50/40">
            <p className="text-[11px] font-bold text-emerald-700">
              Enviando como {SECTOR_LABELS[sector] || sector}
            </p>
            <button
              type="button"
              onClick={() => setPickedSector(null)}
              className="text-[11px] font-bold text-emerald-600 hover:underline"
            >
              Cambiar
            </button>
          </div>
        )}

        {/* Variantes */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-slate-50/50">
          {variants.map((v) => {
            const Icon = v.icon;
            const desc = v.action === "flow_televenta" ? televentaVariantDesc(sector) : v.desc;
            const vSends = sendsByAction[v.action] || [];
            const vEmailSends = emailSendsByAction[v.action] || [];
            const lastOf = (arr) => arr[arr.length - 1];
            const stamp = (arr) => {
              const l = lastOf(arr);
              return [l?.by_name || l?.by_user_id, formatDate(l?.at)].filter(Boolean).join(" · ");
            };
            return (
              <div
                key={v.action}
                className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${ICON_TONES[v.tone]}`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <h4 className="text-sm font-black text-slate-800">{v.title}</h4>
                      {vSends.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 text-[9px] font-black uppercase tracking-wide whitespace-nowrap"
                          title={`Ya se envió${stamp(vSends) ? ` (${stamp(vSends)})` : ""}${vSends.length > 1 ? ` · ${vSends.length} envíos` : ""}`}
                        >
                          <CheckCircle className="w-2.5 h-2.5" />
                          {`Enviado${formatDate(lastOf(vSends)?.at) ? ` · ${formatDate(lastOf(vSends).at)}` : ""}`}
                        </span>
                      )}
                      {vEmailSends.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-100 text-[9px] font-black uppercase tracking-wide whitespace-nowrap"
                          title={`El email que acompaña a este envío ya salió${stamp(vEmailSends) ? ` (${stamp(vEmailSends)})` : ""}`}
                        >
                          <Mail className="w-2.5 h-2.5" />
                          {`Email${formatDate(lastOf(vEmailSends)?.at) ? ` · ${formatDate(lastOf(vEmailSends).at)}` : ""}`}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-medium text-slate-500 leading-snug mt-0.5">
                      {desc}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => onPreview?.(v.action, sector)}
                    disabled={needsSector || sending}
                    title="Vista previa"
                    aria-label={`Vista previa de ${v.title}`}
                    className="px-3 py-2 rounded-xl bg-slate-50 text-slate-500 hover:bg-slate-100 disabled:opacity-40 border border-slate-200/60 transition-all active:scale-95"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onSend?.(v.action, sector)}
                    disabled={needsSector || sending}
                    className={`px-5 py-2 rounded-xl bg-gradient-to-r text-white font-bold text-xs disabled:opacity-40 shadow-lg transition-all active:scale-95 flex items-center gap-1.5 ${TONES[v.tone]}`}
                  >
                    {sending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    {vSends.length > 0 || vEmailSends.length > 0 ? "Reenviar" : "Enviar"}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Sin sector no sabemos si el sector tiene ad, así que no se puede ni ofrecer. */}
          {!needsSector && !hasTeleventaFlow(sector) && (
            <p className="text-center text-[11px] font-medium text-slate-400 pt-1">
              {SECTOR_LABELS[sector] || sector} todavía no tiene vídeo, así que no hay
              variante de televenta.
            </p>
          )}
          {televentaOnly && (
            <p className="text-center text-[11px] font-medium text-slate-400 pt-1">
              Lead de televenta: solo se ofrece su Flow (el del vídeo). Quita la marca
              «Lead de televenta» de la ficha si quieres el resto de variantes.
            </p>
          )}
          {!needsSector && !televentaOnly && !hasMarketingFlow(sector) && (
            <p className="text-center text-[11px] font-medium text-slate-400 pt-1">
              {SECTOR_LABELS[sector] || sector} todavía no tiene vídeo de marketing, así que
              no hay variante de "Flow marketing".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
